import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// Standard shirt size options
const SHIRT_SIZES = [
  'Youth XS', 
  'Youth S', 
  'Youth M', 
  'Youth L', 
  'Youth XL',
  'Adult S', 
  'Adult M', 
  'Adult L', 
  'Adult XL'
];

export default function ShirtSizeSelector() {
  const { playerId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [alreadyHasSize, setAlreadyHasSize] = useState<boolean>(false);
  const [currentSize, setCurrentSize] = useState<string | null>(null);

  useEffect(() => {
    // Validate that we have a player ID
    if (!playerId) {
      setError('Invalid link: Missing player ID');
      setLoading(false);
      return;
    }

    // Check if the player already has a shirt size
    checkPlayerShirtSize();
  }, [playerId]);

  const checkPlayerShirtSize = async () => {
    try {
      setLoading(true);
      
      // Call our Supabase function to check shirt size status
      const { data, error } = await supabase
        .rpc('check_player_shirt_size', { player_id: playerId });

      if (error) throw error;
      
      // If no data or no player found
      if (!data || data.length === 0) {
        setError('Player not found. Please contact the club administration.');
        return;
      }

      // Check if player already has a shirt size
      if (data[0].has_shirt_size) {
        setAlreadyHasSize(true);
        setCurrentSize(data[0].current_size);
      }
    } catch (err) {
      console.error('Error checking shirt size:', err);
      setError('An error occurred while checking the player information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedSize) {
      setError('Please select a shirt size');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Call our Supabase function to update the shirt size
      const { data, error } = await supabase
        .rpc('update_player_shirt_size', { 
          player_id: playerId,
          new_shirt_size: selectedSize 
        });

      if (error) throw error;
      
      // Check if the update was successful
      if (data === true) {
        setSuccess(true);
      } else {
        // This could happen if the shirt size was updated by someone else
        // between our initial check and the update attempt
        await checkPlayerShirtSize(); // Refresh the status
      }
    } catch (err) {
      console.error('Error updating shirt size:', err);
      setError('An error occurred while updating the shirt size. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Success!</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>The shirt size has been successfully updated. Thank you!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (alreadyHasSize) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="rounded-md bg-blue-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">Information</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>This player already has a shirt size selected: <strong>{currentSize}</strong></p>
                    <p className="mt-2">If you believe this is incorrect, please login to the parent portal.</p>
                    <p><a href="https://tryouts.cdosoccer.com/parent" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Login to Parent Portal</a></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form for selecting shirt size
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Select Player Shirt Size
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please select the appropriate shirt size for the player.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Shirt Size
              </label>
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                required
              >
                <option value="">Select a size</option>
                {SHIRT_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {loading ? 'Saving...' : 'Save Shirt Size'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
